import { TaxClassificationEnum } from "@/lib/enum/tax-classification.enum";

/**
 * VAT category code (BT-151, UNTDID 5305 subset used by X-Invoice/EN16931).
 * Member names match the backend's VatCategoryCodeEnum for cross-repo consistency.
 */
export enum TaxCategoryEnum {
  STANDARD = "S",
  ZERO = "Z",
  EXEMPT = "E",
  REVERSE_CHARGE = "AE",
  INTRA_COMMUNITY = "K",
  EXPORT = "G",
  OUT_OF_SCOPE = "O"
}

/** How the tax rate field behaves for a given category. */
export enum TaxRateModeEnum {
  FREE = "free", // editable, no forced value
  ZERO = "zero", // forced to 0, field disabled
  FORBIDDEN = "forbidden" // cleared to undefined, field disabled
}

/** How the reason/code fields behave for a given category. */
export enum TaxReasonModeEnum {
  HIDDEN = "hidden", // fields not rendered at all
  FORBIDDEN = "forbidden", // must stay empty
  REQUIRED = "required" // at least one of reason/code must be filled
}

export interface TaxCategoryRule {
  rate: TaxRateModeEnum;
  reason: TaxReasonModeEnum;
}

export const TAX_CATEGORY_RULES: Record<TaxCategoryEnum, TaxCategoryRule> = {
  [TaxCategoryEnum.STANDARD]: { rate: TaxRateModeEnum.FREE, reason: TaxReasonModeEnum.HIDDEN },
  [TaxCategoryEnum.ZERO]: { rate: TaxRateModeEnum.ZERO, reason: TaxReasonModeEnum.FORBIDDEN },
  [TaxCategoryEnum.EXEMPT]: { rate: TaxRateModeEnum.ZERO, reason: TaxReasonModeEnum.REQUIRED },
  [TaxCategoryEnum.REVERSE_CHARGE]: { rate: TaxRateModeEnum.ZERO, reason: TaxReasonModeEnum.REQUIRED },
  [TaxCategoryEnum.INTRA_COMMUNITY]: { rate: TaxRateModeEnum.ZERO, reason: TaxReasonModeEnum.REQUIRED },
  [TaxCategoryEnum.EXPORT]: { rate: TaxRateModeEnum.ZERO, reason: TaxReasonModeEnum.REQUIRED },
  [TaxCategoryEnum.OUT_OF_SCOPE]: { rate: TaxRateModeEnum.FORBIDDEN, reason: TaxReasonModeEnum.REQUIRED }
};

/**
 * Default VATEX exemption code for categories with an unambiguous 1:1 mapping — per the VATEX
 * codes' own "Only use with VAT category code X" text on the official Peppol codelist
 * (https://docs.peppol.eu/poacc/billing/3.0/codelist/vatex/, see VATEX_CODE_INFO in
 * vatex-code.constant.ts). This is a prefill only, never enforced — the user can freely overwrite
 * it. Categories not listed here (Exempt, in particular) have several valid VATEX codes depending
 * on the specific reason (second-hand goods, works of art, collectors' items, ...), so there's no
 * single default to offer. French (VATEX-FR-*) codes are excluded for the same reason: several
 * apply to Exempt/Standard depending on the specific French tax article, not a fixed category.
 */
export const DEFAULT_VATEX_CODE_BY_CATEGORY: Partial<Record<TaxCategoryEnum, string>> = {
  [TaxCategoryEnum.REVERSE_CHARGE]: "VATEX-EU-AE",
  [TaxCategoryEnum.INTRA_COMMUNITY]: "VATEX-EU-IC",
  [TaxCategoryEnum.EXPORT]: "VATEX-EU-G",
  [TaxCategoryEnum.OUT_OF_SCOPE]: "VATEX-EU-O"
};

/**
 * Deliberately NOT typed against IInvoiceLine/IProduct here: those entities import this module
 * for TaxCategoryEnum/TAX_CATEGORY_RULES, so importing them back would be circular.
 */
export interface TaxCategoryFields {
  taxCategory?: TaxCategoryEnum | null;
  taxExemptionReason?: string | null;
  taxExemptionReasonCode?: string | null;
}

/**
 * `insurance` maps to the exempt defaults, sourced from CreateProductDto's own @example values
 * (taxCategory E / VATEX-EU-135-1 / "Steuerbefreit nach §4 Nr. 11 UStG"), not inferred. Every
 * other classification (including small_business, and unset) defaults to Standard — the ordinary
 * case, and what a new invoice line/product otherwise has no category for.
 */
export function taxDefaultsFromClassification(
  classification?: TaxClassificationEnum | null
): TaxCategoryFields | undefined {
  if (classification !== TaxClassificationEnum.INSURANCE) {
    return {
      taxCategory: TaxCategoryEnum.STANDARD
    };
  }

  return {
    taxCategory: TaxCategoryEnum.EXEMPT,
    taxExemptionReasonCode: "VATEX-EU-135-1",
    taxExemptionReason: "Steuerbefreit nach §4 Nr. 11 UStG"
  };
}

/**
 * A product with no taxCategory of its own always passes (it just inherits the seller's
 * default). A product with an explicit taxCategory passes only if it matches what the
 * seller's classification implies — e.g. a Standard-rate product is flagged for a seller
 * classified as an insurance business, which should never charge standard VAT.
 */
export function isProductTaxCompatible(
  product: { taxCategory?: TaxCategoryEnum | null },
  sellerClassification?: TaxClassificationEnum | null
): boolean {
  const expected = taxDefaultsFromClassification(sellerClassification)?.taxCategory;
  if (!expected || !product.taxCategory) {
    return true;
  }
  return product.taxCategory === expected;
}

/**
 * A tax rate as a form field actually holds it: a number once one is picked, but null or undefined
 * before anything is entered, and the empty string a cleared Vuetify number field leaves behind
 * (`v-model.number` hands back the raw "" when it does not parse). None of those is a rate of 0,
 * and the helpers below have to tell them apart, so the type says so rather than casting.
 */
export type TaxRateFieldValue = number | string | null | undefined;

/**
 * Whether a line carries the zero rate — an explicit 0, not merely a missing one. A line whose rate
 * is still empty has no rate to explain yet; the rate field's own required rule is what asks for
 * one.
 */
export function isZeroTaxRate(taxRate: TaxRateFieldValue): boolean {
  return taxRate === 0;
}

/**
 * The categories offered for a given rate. Standard is the category for actually charging VAT, so
 * it is not offered at a 0 rate: a line that charges nothing has to name the reason (zero-rated,
 * exempt, reverse charge, ...). Exactly the combination the invoice form's taxCategoryZeroRateRule
 * rejects, kept out of the picker rather than only flagged after the fact.
 */
export function taxCategoriesForRate(taxRate: TaxRateFieldValue): TaxCategoryEnum[] {
  const categories = Object.values(TaxCategoryEnum);

  return isZeroTaxRate(taxRate) ? categories.filter((category) => category !== TaxCategoryEnum.STANDARD) : categories;
}

/**
 * Keeps the VAT category in step with the rate on the line, for the direction the category select
 * does not cover ({@link TAX_CATEGORY_RULES} and the form's onTaxCategoryChanged handle the other).
 *
 * A non-zero rate is the ordinary case and needs no explanation, so the line takes Standard and
 * drops exemption text left over from a category that did need one. A 0 rate is the opposite:
 * Standard cannot explain it, so the category is cleared for the user to pick the one that does,
 * in the select the 0 itself brings up.
 *
 * A line with no rate at all is left alone — an empty field says nothing about the category, and a
 * category that forbids a rate outright (Out of scope) is what emptied it in the first place.
 */
export function applyTaxCategoryForRate(line: TaxCategoryFields & { taxRate?: TaxRateFieldValue }): void {
  // Nothing to conclude from a rate the user has not filled in — see TaxRateFieldValue for the
  // three shapes an empty field takes, none of which is a rate of 0.
  if (line.taxRate == null || line.taxRate === "") {
    return;
  }

  if (isZeroTaxRate(line.taxRate)) {
    if (line.taxCategory === TaxCategoryEnum.STANDARD) {
      line.taxCategory = undefined;
    }

    return;
  }

  if (line.taxCategory === TaxCategoryEnum.STANDARD) {
    return;
  }

  line.taxCategory = TaxCategoryEnum.STANDARD;
  line.taxExemptionReason = undefined;
  line.taxExemptionReasonCode = undefined;
}
